import React, { useEffect, useMemo, useState } from 'react';
import { Box, IconButton, InputBase, Tooltip } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import ContentCopyRounded from '@mui/icons-material/ContentCopyRounded';
import CheckRounded from '@mui/icons-material/CheckRounded';
import { tokenizeCode, type CodeLanguage, type CodeTokenKind } from '../utils/highlight';

export interface CodeBlockProps {
  code: string;
  /** Colours the code by part — keywords, names, strings, numbers, comments. Plain by default. */
  language?: CodeLanguage;
  /** A gutter of line numbers, for anything longer than a few lines. */
  lineNumbers?: boolean;
  /** Makes the block editable; called with the new text on every change. */
  onChange?: (code: string) => void;
  /** A Copy button in the corner. Not shown while editing. */
  copyable?: boolean;
  /** Scroll past this height, px, instead of growing. */
  maxHeight?: number;
  'aria-label'?: string;
}

const codeSx = (t: Theme) => ({
  ...t.typography.caption,
  fontFamily: t.typography.fontFamilyMono,
  lineHeight: 1.7,
  color: t.tokens.text.primary,
  bgcolor: t.tokens.chat.codeBg,
  borderRadius: `${t.ds.radius.sm}px`,
});

/** Colour for each kind of code text, from the theme so it follows dark mode. */
const tokenColor = (t: Theme, kind: CodeTokenKind): string | undefined => {
  switch (kind) {
    case 'keyword':
    case 'key':
      return t.tokens.text.accent;
    case 'function':
      return t.tokens.text.secondary;
    case 'string':
      return t.tokens.semantic.success;
    case 'number':
    case 'literal':
      return t.tokens.semantic.warning;
    case 'comment':
    case 'punctuation':
    case 'operator':
      return t.tokens.text.tertiary;
    default:
      return undefined;
  }
};

/**
 * A block of code or SQL, shown as written: monospace, on a recessed ground,
 * scrolling sideways rather than wrapping so lines keep their shape. With a
 * `language` it is coloured by part; `lineNumbers` adds a gutter. Pass
 * `onChange` to let someone edit it in place, e.g. a query before it runs.
 *
 * Formatting (line breaks, indentation) is the caller's: this shows the code
 * it is given. For code inside model text, use `Markdown`.
 */
export function CodeBlock({
  code,
  language = 'text',
  lineNumbers = false,
  onChange,
  copyable,
  maxHeight,
  'aria-label': ariaLabel,
}: CodeBlockProps) {
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return;
    const id = setTimeout(() => setCopied(false), 1500);
    return () => clearTimeout(id);
  }, [copied]);

  const tokens = useMemo(() => tokenizeCode(code, language), [code, language]);
  const lineCount = useMemo(() => code.split('\n').length, [code]);

  if (onChange) {
    return (
      <InputBase
        multiline
        minRows={4}
        fullWidth
        value={code}
        onChange={(e) => onChange(e.target.value)}
        inputProps={{ 'aria-label': ariaLabel, spellCheck: false }}
        sx={(t) => ({
          ...codeSx(t),
          p: 1.5,
          border: `1px solid ${t.tokens.chat.inputFocusBorder}`,
          '& textarea': { whiteSpace: 'pre', overflowX: 'auto', maxHeight, fontFamily: 'inherit' },
        })}
      />
    );
  }

  return (
    <Box sx={{ position: 'relative' }}>
      <Box
        sx={(t) => ({
          ...codeSx(t),
          display: 'flex',
          overflow: 'auto',
          maxHeight,
        })}
      >
        {lineNumbers && (
          <Box
            aria-hidden
            sx={(t) => ({
              flexShrink: 0,
              py: 1.5,
              pl: 1.5,
              pr: 1.25,
              textAlign: 'right',
              color: t.tokens.text.disabled,
              borderRight: `1px solid ${t.tokens.border.subtle}`,
              userSelect: 'none',
              fontVariantNumeric: 'tabular-nums',
              whiteSpace: 'pre',
            })}
          >
            {Array.from({ length: lineCount }, (_, i) => i + 1).join('\n')}
          </Box>
        )}
        <Box
          component="pre"
          aria-label={ariaLabel}
          sx={{ m: 0, py: 1.5, pl: 1.5, pr: copyable ? 5 : 1.5, whiteSpace: 'pre', flex: 1, minWidth: 0, font: 'inherit' }}
        >
          <Box component="code" sx={{ font: 'inherit' }}>
            {language === 'text'
              ? code
              : tokens.map((tok, i) => {
                  const kind = tok.kind;
                  if (kind === 'plain') return tok.text;
                  return (
                    <Box
                      key={i}
                      component="span"
                      sx={(t) => ({
                        color: tokenColor(t, kind),
                        fontWeight: kind === 'keyword' ? 600 : undefined,
                        fontStyle: kind === 'comment' ? 'italic' : undefined,
                      })}
                    >
                      {tok.text}
                    </Box>
                  );
                })}
          </Box>
        </Box>
      </Box>
      {copyable && (
        <Tooltip title={copied ? 'Copied' : 'Copy'}>
          <IconButton
            size="small"
            aria-label={copied ? 'Copied' : 'Copy'}
            onClick={() => {
              void navigator.clipboard?.writeText(code).then(() => setCopied(true), () => undefined);
            }}
            sx={(t) => ({
              position: 'absolute',
              top: t.spacing(0.75),
              right: t.spacing(0.75),
              color: t.tokens.text.tertiary,
              bgcolor: t.tokens.chat.codeBg,
              '&:hover': { color: t.tokens.text.primary, bgcolor: t.tokens.chat.codeBg },
            })}
          >
            {copied ? <CheckRounded fontSize="small" /> : <ContentCopyRounded fontSize="small" />}
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
}
